// Main navigation drawer toggle (mobile) and submenu dropdowns
// (desktop hover/focus, touch/keyboard click). Progressive enhancement:
// desktop dropdowns work even with JS disabled they'd just stay closed,
// the underlying links remain reachable in the flat drawer list markup.
//
// Visibility keys only on the item's own data-open attribute, set here,
// never on a CSS :hover/:focus-within match. That is what makes Escape
// actually dismiss the panel (WCAG 1.4.13): with a pure-CSS hover panel,
// removing an attribute cannot override a live :hover match, so Escape
// would do nothing while the pointer keeps hovering.
const OPEN_DELAY = 120;
const CLOSE_DELAY = 150;

function initNav(): void {
  document.querySelectorAll<HTMLElement>('[data-th-nav]').forEach((nav) => {
    const toggle = nav.querySelector<HTMLButtonElement>('.th-menu-btn');
    const closeDrawer = () => {
      nav.removeAttribute('data-open');
      toggle?.setAttribute('aria-expanded', 'false');
    };
    toggle?.addEventListener('click', () => {
      const open = nav.toggleAttribute('data-open');
      toggle.setAttribute('aria-expanded', String(open));
    });
    // Tabbing (or shift-tabbing) past the drawer's last/first focusable
    // element, e.g. into page content, has to close it too: the drawer
    // otherwise stays open (and its links stay in the tab order) even
    // though it visually looks abandoned once focus has moved on.
    nav.addEventListener('focusout', (event) => {
      const next = event.relatedTarget as Node | null;
      if (nav.hasAttribute('data-open') && (!next || !nav.contains(next))) closeDrawer();
    });

    let timer = 0;

    const setItemOpen = (item: HTMLElement, open: boolean) => {
      item.toggleAttribute('data-open', open);
      item.querySelector<HTMLButtonElement>('.th-nav__caret')?.setAttribute('aria-expanded', String(open));
    };
    const closeAll = (except?: HTMLElement) => {
      nav.querySelectorAll<HTMLElement>('.th-nav__item--has-children[data-open]').forEach((item) => {
        if (item !== except) setItemOpen(item, false);
      });
    };
    const openNow = (item: HTMLElement) => {
      window.clearTimeout(timer);
      closeAll(item);
      setItemOpen(item, true);
    };
    const scheduleOpen = (item: HTMLElement) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => openNow(item), OPEN_DELAY);
    };
    const scheduleClose = (item: HTMLElement) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setItemOpen(item, false), CLOSE_DELAY);
    };

    nav.querySelectorAll<HTMLElement>('.th-nav__item--has-children').forEach((item) => {
      const link = item.querySelector<HTMLAnchorElement>('.th-nav__item-row a');
      const caret = item.querySelector<HTMLButtonElement>('.th-nav__caret');

      item.addEventListener('mouseenter', () => scheduleOpen(item));
      item.addEventListener('mouseleave', () => scheduleClose(item));
      link?.addEventListener('focus', () => openNow(item));
      // Moving focus between the link, the caret and the submenu links
      // stays inside the item: only close once focus leaves it for good.
      item.addEventListener('focusout', (event) => {
        const next = event.relatedTarget as Node | null;
        if (!next || !item.contains(next)) scheduleClose(item);
      });
      caret?.addEventListener('click', () => {
        setItemOpen(item, !item.hasAttribute('data-open'));
      });
    });

    document.addEventListener('click', (event) => {
      if (!nav.contains(event.target as Node)) {
        closeAll();
        if (nav.hasAttribute('data-open')) closeDrawer();
      }
    });
    nav.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const activeItem = (event.target as HTMLElement)?.closest?.('.th-nav__item--has-children') as HTMLElement | null;
      // Escape closes the innermost open thing first: an open submenu,
      // refocusing its caret; only the plain drawer closes the drawer,
      // refocusing the toggle button so keyboard context is not lost.
      if (activeItem?.hasAttribute('data-open')) {
        activeItem.querySelector<HTMLButtonElement>('.th-nav__caret')?.focus();
        window.clearTimeout(timer);
        closeAll();
        return;
      }
      if (nav.hasAttribute('data-open')) {
        closeDrawer();
        toggle?.focus();
      }
    });
  });
}
document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', initNav) : initNav();
